import Dexie, { Table } from 'dexie';
import type { MeasureUnit } from '../config/units';

// TypeScript interfaces matching PRD data models
export interface Project {
  id: string; // UUID
  title: string;
  client: string;
  address: string;
  notes: string;
  floors: string[]; // e.g., ['-1', '0', '1', '2']
  plans: string[]; // URLs or local blob IDs
  useRoomNumbering: boolean; // Switch Stanza
  useInterventionNumbering: boolean; // Switch Intervento n.
  typologies: Typology[];
  ownerId: string; // user UUID
  accessibleUsers: string[]; // array of user UUIDs
  archived: number; // 0 = false, 1 = true (for Dexie indexing compatibility)
  syncEnabled: number; // 0 = false (metadata only), 1 = true (full sync with photos and mappings)
  createdAt: number; // timestamp
  updatedAt: number; // timestamp
  version?: number; // For conflict detection (optional for backward compatibility)
  lastModified?: number; // For conflict detection (optional for backward compatibility)
  synced: number; // 0 = false, 1 = true (for Dexie indexing compatibility)
}

export interface Typology {
  id: string;
  number: number;
  category?: 'attraversamento' | 'struttura'; // undefined = 'attraversamento' (retrocompatibile)
  supporto: string;
  tipoSupporto: string;
  attraversamento: string;
  attraversamentoCustom?: string;
  struttura?: string;       // solo per category='struttura'
  tipoStruttura?: string;   // solo per category='struttura'
  marcaProdottoUtilizzato: string;
  prodottiSelezionati: string[];
}

export interface PhotoMetadata {
  id: string;
  localBlobId?: string; // If not yet uploaded
  remoteUrl?: string; // After upload
  timestamp: number;
  size: number;
  compressed: boolean;
}

export interface Crossing {
  id: string;
  supporto: string;
  tipoSupporto: string;
  attraversamento: string;
  attraversamentoCustom?: string;
  tipologicoId?: string;
  unit?: MeasureUnit; // unità della voce attraversamento; default 'pz'
  quantita?: number;
  lunghezza?: number; // ml/m (unit 'ml' | 'm')
  superficie?: number; // mq (unit 'mq', non-asola)
  diametro?: string;
  dimensioni?: string;
  notes?: string;
  inAsola?: boolean;  // true se l'attraversamento passa in un'asola
  asolaB?: number;    // larghezza asola in cm
  asolaH?: number;    // altezza asola in cm
  salId?: string;     // UUID del SAL a cui è assegnato (undefined = non contabilizzato)
}

// Calcola area asola in mq con minimo di 0,2 mq
export function calcAsolaMq(b: number, h: number): number {
  return Math.max(0.2, (b * h) / 10000);
}

// ============================================
// STRUTTURE INTERFACES
// ============================================

// Una "parte" di una struttura misurata in mq: il muro ad angolo = più parti sommate.
export interface StrutturaParte {
  id: string;
  base?: number;              // m (larghezza)
  altezza?: number;           // m (altezza)
}

export interface Structure {
  id: string;
  struttura: string;          // "Parete" | "Soffitto" | "Cassonetto porta-impianto" | "Altro"
  strutturaCustom?: string;   // usato quando struttura === 'Altro'
  tipoStruttura?: string;     // es. "Flessibile", "Rigido"
  tipologicoId?: string;      // → Project.typologies (category='struttura')
  unit?: MeasureUnit;         // unità della voce struttura (default 'mq'); guida i campi input
  parti?: StrutturaParte[];   // unit 'mq': righe base×altezza, superficie = somma
  base?: number;              // m (larghezza) — legacy/compat; ora vive in parti[0]
  altezza?: number;           // m (altezza) — legacy/compat
  superficie?: number;        // mq totale (somma delle parti)
  lunghezza?: number;         // ml/m (unit 'ml' | 'm')
  quantita?: number;          // pz (unit 'pz')
  notes?: string;
  salId?: string;             // UUID del SAL a cui è assegnato (undefined = non contabilizzato)
}

export interface StructureEntry {
  id: string;
  projectId: string;
  floor: string;
  room?: string;
  intervention?: string;
  photoIds: string[]; // v14: solo riferimenti alla tabella `photos` (fonte di verità)
  structures: Structure[];
  toComplete?: boolean;
  timestamp: number;
  createdBy: string;
  lastModified: number;
  modifiedBy: string;
  version: number;
  synced: 0 | 1;
  hasRemotePhotos?: boolean;
}

export interface MappingEntry {
  id: string; // UUID
  projectId: string;
  floor: string;
  room?: string;
  intervention?: string;
  photoIds: string[]; // v14: solo riferimenti alla tabella `photos` (fonte di verità)
  crossings: Crossing[];
  toComplete?: boolean; // Flag to mark if intervention is still to be completed
  timestamp: number;
  createdBy: string; // user UUID
  lastModified: number;
  modifiedBy: string;
  version: number; // For conflict detection
  synced: number; // 0 = false, 1 = true (for Dexie indexing compatibility)
  hasRemotePhotos?: boolean; // True when server has photos not downloaded locally
}

export interface Photo {
  id: string;
  blob?: Blob;
  thumbnailBlob?: Blob;
  mappingEntryId: string;
  entryType?: 'mapping' | 'structure'; // undefined = 'mapping' (retrocompatibile)
  metadata: {
    width: number;
    height: number;
    size: number;
    mimeType: string;
    captureTimestamp: number;
    gps?: { lat: number; lon: number }; // Optional
  };
  uploaded: boolean;
  remoteUrl?: string;
  thumbnailRemoteUrl?: string;
  storagePath?: string;
  thumbnailStoragePath?: string;
}

export interface SyncQueueItem {
  id: string;
  operation: 'CREATE' | 'UPDATE' | 'DELETE';
  entityType: 'project' | 'mapping_entry' | 'photo' | 'floor_plan' | 'floor_plan_point' | 'standalone_map' | 'sal' | 'typology_price' | 'structure_entry';
  entityId: string;
  payload: any; // The actual data to sync
  timestamp: number;
  retryCount: number;
  lastError?: string;
  lastAttemptAt?: number; // Timestamp of last retry attempt (for exponential backoff)
  synced: number; // 0 = pending, 1 = synced, 2 = permanently failed
}

export interface User {
  id: string; // UUID
  email: string;
  username: string; // Display name
  role: 'admin' | 'user';
  createdAt: number;
}

export interface AppMetadata {
  key: string;
  value: any;
}

export interface ProjectCachePref {
  projectId: string;
  offlinePinned: number;
  lastHydratedAt?: number;
  updatedAt: number;
}

export interface ConflictHistory {
  id: string;
  timestamp: number;
  entityType: 'project' | 'mapping_entry' | 'floor_plan' | 'floor_plan_point';
  entityId: string;
  conflictType: 'version' | 'timestamp' | 'both';
  localVersion: any;
  remoteVersion: any;
  resolvedVersion: any;
  strategy: string; // ConflictResolutionStrategy
  autoResolved: boolean;
  userNotified: boolean;
}

// ============================================
// FLOOR PLAN INTERFACES
// ============================================

export interface FloorPlan {
  id: string;
  projectId: string;
  floor: string;
  imageBlob?: Blob; // Optional in online-first mode; downloaded lazily
  thumbnailBlob?: Blob; // Store thumbnail locally
  imageUrl?: string; // Supabase URL (when synced)
  thumbnailUrl?: string; // Supabase URL (when synced)
  pdfBlobBase64?: string; // PDF originale come Base64 (solo se input era PDF) — per export vettoriale
  pdfUrl?: string; // Supabase Storage URL del PDF originale (quando sincronizzato)
  originalFilename: string;
  originalFormat: string;
  width: number;
  height: number;
  gridEnabled?: boolean;
  gridConfig?: {
    rows: number;
    cols: number;
    offsetX: number;
    offsetY: number;
  };
  metadata?: Record<string, any>;
  createdBy: string;
  createdAt: number; // timestamp
  updatedAt: number; // timestamp
  remoteUpdatedAt?: number; // timestamp of remote updated_at at last sync (for conflict detection)
  assetDirty?: 0 | 1; // 1 when blobs changed locally and storage upload must be refreshed
  synced: 0 | 1; // 0 = not synced, 1 = synced
}

export interface FloorPlanPoint {
  id: string;
  floorPlanId: string;
  mappingEntryId?: string;      // opzionale: usato quando il punto è collegato a un MappingEntry
  structureEntryId?: string;    // opzionale: usato quando il punto è collegato a un StructureEntry
  pointType: 'parete' | 'solaio' | 'perimetro' | 'generico' | 'struttura';
  pointX: number; // Normalized 0-1
  pointY: number; // Normalized 0-1
  labelX: number; // Normalized 0-1
  labelY: number; // Normalized 0-1
  perimeterPoints?: Array<{ x: number; y: number }>; // For 'perimetro' type
  customText?: string; // For 'generico' type
  eiRating?: 30 | 60 | 90 | 120 | 180 | 240; // Fire resistance rating (EI)
  metadata?: Record<string, any>;
  createdBy: string;
  createdAt: number; // timestamp
  updatedAt: number; // timestamp
  remoteUpdatedAt?: number; // timestamp of remote updated_at at last sync (for conflict detection)
  synced: 0 | 1;
}

export interface StandaloneMap {
  id: string;
  userId: string;
  name: string;
  description?: string;
  imageBlob?: Blob;
  thumbnailBlob?: Blob;
  pdfBlobBase64?: string;
  imageUrl?: string; // Supabase URL (when synced)
  thumbnailUrl?: string;
  pdfUrl?: string;
  originalFilename: string;
  originalFormat?: string;
  width: number;
  height: number;
  points: Array<{
    id: string;
    pointType: 'parete' | 'solaio' | 'perimetro' | 'generico' | 'struttura';
    pointX: number;
    pointY: number;
    labelX: number;
    labelY: number;
    perimeterPoints?: Array<{ x: number; y: number }>;
    customText?: string;
    eiRating?: 30 | 60 | 90 | 120 | 180 | 240;
    labelBackgroundColor?: string; // Custom background color for label
    labelTextColor?: string; // Custom text color for label
    labelText?: string[]; // Custom label text (if modified by user)
  }>;
  gridEnabled: boolean;
  gridConfig: {
    rows: number;
    cols: number;
    offsetX: number;
    offsetY: number;
  };
  metadata?: Record<string, any>;
  createdAt: number;
  updatedAt: number;
  synced: 0 | 1;
}

// ============================================
// TYPOLOGY PRICES INTERFACE
// ============================================

export interface TypologyPrice {
  id: string;
  projectId: string;
  category?: 'attraversamento' | 'struttura'; // undefined = 'attraversamento' (retrocompatibile)
  attraversamento: string;  // chiave: tipo (es. "Tubo metallico NUDO", "Parete")
  tipologicoId?: string;
  pricePerUnit: number;
  unit: MeasureUnit;
  createdAt?: number;
  updatedAt?: number;
  synced?: 0 | 1;
}

// ============================================
// SAL (STATO AVANZAMENTO LAVORI) INTERFACE
// ============================================

export interface Sal {
  id: string;
  projectId: string;
  number: number;       // Progressivo (1, 2, 3...)
  name?: string;        // Es. "SAL 1 — Marzo 2026"
  date: number;         // Timestamp data chiusura
  notes?: string;
  createdAt: number;
  synced: 0 | 1;
}

// ============================================
// AUTH CACHE INTERFACE (PBKDF2 offline auth)
// ============================================

export interface AuthCache {
  id: string;         // email normalizzata (PK)
  email: string;
  salt: string;       // base64
  iv: string;         // base64
  ciphertext: string; // base64
  createdAt: number;  // epoch ms
  expiresAt: number;  // epoch ms (createdAt + 7gg)
}

// ============================================
// REALTIME STATE
// ============================================

// Tabella KV generica persistente. Usata da:
//   - eventStream (chiavi: 'appliedSeq')
//   - auth.ts offline rate-limit (chiavi: 'offline_fails_<email>')
// Le chiavi sono namespace-isolated per convenzione; non aggiungere chiavi senza prefisso.
export interface PersistentKVEntry {
  key: string;   // PK, es. 'appliedSeq' | 'tabId' | 'offline_fails_<email>'
  value: string; // stringa (seq come string BigInt, tabId come UUID, counter come stringa)
}

// ============================================
// DROPDOWN OPTIONS CACHE INTERFACES
// ============================================

export interface DropdownOptionCache {
  id: string; // Supabase UUID
  category: string; // e.g., 'supporto', 'tipo_supporto', 'attraversamento'
  value: string;
  label: string;
  unit?: MeasureUnit; // unità di misura per voce (struttura/attraversamento); undefined = default categoria
  sortOrder: number;
  isActive: boolean;
  fetchedAt: number; // timestamp
}

export interface ProductCache {
  id: string; // Supabase UUID
  brand: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
  fetchedAt: number; // timestamp
}

// Dexie database class
export class MappingDatabase extends Dexie {
  // Typed table properties
  projects!: Table<Project, string>;
  mappingEntries!: Table<MappingEntry, string>;
  photos!: Table<Photo, string>;
  syncQueue!: Table<SyncQueueItem, string>;
  users!: Table<User, string>;
  metadata!: Table<AppMetadata, string>;
  projectCachePrefs!: Table<ProjectCachePref, string>;
  conflictHistory!: Table<ConflictHistory, string>;

  // FLOOR PLAN TABLES
  floorPlans!: Table<FloorPlan, string>;
  floorPlanPoints!: Table<FloorPlanPoint, string>;
  standaloneMaps!: Table<StandaloneMap, string>;

  // DROPDOWN OPTIONS CACHE
  dropdownOptionsCache!: Table<DropdownOptionCache, string>;
  productsCache!: Table<ProductCache, string>;

  // TYPOLOGY PRICES
  typologyPrices!: Table<TypologyPrice, string>;

  // SAL (STATO AVANZAMENTO LAVORI)
  sals!: Table<Sal, string>;

  // STRUTTURE
  structureEntries!: Table<StructureEntry, string>;

  // AUTH CACHE (PBKDF2 offline auth)
  authCache!: Table<AuthCache, string>;

  // REALTIME STATE / KV persistente (seq cursor SSE, offline rate-limit auth)
  realtimeState!: Table<PersistentKVEntry, string>;

  constructor() {
    super('MappingDatabase');

    // Define schema v1
    this.version(1).stores({
      projects: 'id, ownerId, *accessibleUsers, synced, updatedAt',
      mappingEntries: 'id, projectId, floor, createdBy, synced, timestamp',
      photos: 'id, mappingEntryId, uploaded',
      syncQueue: 'id, synced, timestamp, entityType, entityId',
      users: 'id, email, role',
      metadata: 'key'
    });

    // Define schema v2 - add archived field to projects
    this.version(2).stores({
      projects: 'id, ownerId, *accessibleUsers, synced, updatedAt, archived',
      mappingEntries: 'id, projectId, floor, createdBy, synced, timestamp',
      photos: 'id, mappingEntryId, uploaded',
      syncQueue: 'id, synced, timestamp, entityType, entityId',
      users: 'id, email, role',
      metadata: 'key'
    }).upgrade(tx => {
      // Set archived = 0 for all existing projects
      return tx.table('projects').toCollection().modify(project => {
        if (project.archived === undefined) {
          project.archived = 0;
        }
      });
    });

    // Define schema v3 - add syncEnabled field to projects and conflictHistory table
    this.version(3).stores({
      projects: 'id, ownerId, *accessibleUsers, synced, updatedAt, archived, syncEnabled',
      mappingEntries: 'id, projectId, floor, createdBy, synced, timestamp',
      photos: 'id, mappingEntryId, uploaded',
      syncQueue: 'id, synced, timestamp, entityType, entityId',
      users: 'id, email, role',
      metadata: 'key',
      conflictHistory: 'id, timestamp, entityType, entityId, userNotified'
    }).upgrade(tx => {
      // Set syncEnabled = 0 for all existing projects (default to metadata-only sync)
      return tx.table('projects').toCollection().modify(project => {
        if (project.syncEnabled === undefined) {
          project.syncEnabled = 0;
        }
      });
    });

    // Define schema v4 - add floor plan tables
    this.version(4).stores({
      projects: 'id, ownerId, *accessibleUsers, synced, updatedAt, archived, syncEnabled',
      mappingEntries: 'id, projectId, floor, createdBy, synced, timestamp',
      photos: 'id, mappingEntryId, uploaded',
      syncQueue: 'id, synced, timestamp, entityType, entityId',
      users: 'id, email, role',
      metadata: 'key',
      conflictHistory: 'id, timestamp, entityType, entityId, userNotified',
      // NEW TABLES FOR FLOOR PLANS
      floorPlans: 'id, projectId, floor, createdBy, synced, [projectId+floor]',
      floorPlanPoints: 'id, floorPlanId, mappingEntryId, pointType, synced',
      standaloneMaps: 'id, userId, name, synced'
    });

    // Define schema v5 - add dropdown options and products cache tables
    this.version(5).stores({
      projects: 'id, ownerId, *accessibleUsers, synced, updatedAt, archived, syncEnabled',
      mappingEntries: 'id, projectId, floor, createdBy, synced, timestamp',
      photos: 'id, mappingEntryId, uploaded',
      syncQueue: 'id, synced, timestamp, entityType, entityId',
      users: 'id, email, role',
      metadata: 'key',
      conflictHistory: 'id, timestamp, entityType, entityId, userNotified',
      floorPlans: 'id, projectId, floor, createdBy, synced, [projectId+floor]',
      floorPlanPoints: 'id, floorPlanId, mappingEntryId, pointType, synced',
      standaloneMaps: 'id, userId, name, synced',
      // DROPDOWN OPTIONS CACHE
      dropdownOptionsCache: 'id, category, sortOrder',
      productsCache: 'id, brand, sortOrder'
    });

    // Define schema v6 - add typologyPrices table
    this.version(6).stores({
      projects: 'id, ownerId, *accessibleUsers, synced, updatedAt, archived, syncEnabled',
      mappingEntries: 'id, projectId, floor, createdBy, synced, timestamp',
      photos: 'id, mappingEntryId, uploaded',
      syncQueue: 'id, synced, timestamp, entityType, entityId',
      users: 'id, email, role',
      metadata: 'key',
      conflictHistory: 'id, timestamp, entityType, entityId, userNotified',
      floorPlans: 'id, projectId, floor, createdBy, synced, [projectId+floor]',
      floorPlanPoints: 'id, floorPlanId, mappingEntryId, pointType, synced',
      standaloneMaps: 'id, userId, name, synced',
      dropdownOptionsCache: 'id, category, sortOrder',
      productsCache: 'id, brand, sortOrder',
      // TYPOLOGY PRICES
      typologyPrices: 'id, projectId, tipologicoId, [projectId+tipologicoId]'
    });

    // Define schema v7 - change typologyPrices key from tipologicoId to attraversamento
    this.version(7).stores({
      projects: 'id, ownerId, *accessibleUsers, synced, updatedAt, archived, syncEnabled',
      mappingEntries: 'id, projectId, floor, createdBy, synced, timestamp',
      photos: 'id, mappingEntryId, uploaded',
      syncQueue: 'id, synced, timestamp, entityType, entityId',
      users: 'id, email, role',
      metadata: 'key',
      conflictHistory: 'id, timestamp, entityType, entityId, userNotified',
      floorPlans: 'id, projectId, floor, createdBy, synced, [projectId+floor]',
      floorPlanPoints: 'id, floorPlanId, mappingEntryId, pointType, synced',
      standaloneMaps: 'id, userId, name, synced',
      dropdownOptionsCache: 'id, category, sortOrder',
      productsCache: 'id, brand, sortOrder',
      // TYPOLOGY PRICES — keyed by attraversamento string
      typologyPrices: 'id, projectId, attraversamento, [projectId+attraversamento]'
    }).upgrade(tx => {
      // Clear old tipologicoId-based price data (incompatible schema)
      return tx.table('typologyPrices').clear();
    });

    // Define schema v8 - add SAL (Stato Avanzamento Lavori) table
    this.version(8).stores({
      projects: 'id, ownerId, *accessibleUsers, synced, updatedAt, archived, syncEnabled',
      mappingEntries: 'id, projectId, floor, createdBy, synced, timestamp',
      photos: 'id, mappingEntryId, uploaded',
      syncQueue: 'id, synced, timestamp, entityType, entityId',
      users: 'id, email, role',
      metadata: 'key',
      conflictHistory: 'id, timestamp, entityType, entityId, userNotified',
      floorPlans: 'id, projectId, floor, createdBy, synced, [projectId+floor]',
      floorPlanPoints: 'id, floorPlanId, mappingEntryId, pointType, synced',
      standaloneMaps: 'id, userId, name, synced',
      dropdownOptionsCache: 'id, category, sortOrder',
      productsCache: 'id, brand, sortOrder',
      typologyPrices: 'id, projectId, attraversamento, [projectId+attraversamento]',
      // SAL (Stato Avanzamento Lavori)
      sals: 'id, projectId, number, createdAt'
    });

    // Define schema v9 - allow prices specific to attraversamento + tipologico
    this.version(9).stores({
      projects: 'id, ownerId, *accessibleUsers, synced, updatedAt, archived, syncEnabled',
      mappingEntries: 'id, projectId, floor, createdBy, synced, timestamp',
      photos: 'id, mappingEntryId, uploaded',
      syncQueue: 'id, synced, timestamp, entityType, entityId',
      users: 'id, email, role',
      metadata: 'key',
      conflictHistory: 'id, timestamp, entityType, entityId, userNotified',
      floorPlans: 'id, projectId, floor, createdBy, synced, [projectId+floor]',
      floorPlanPoints: 'id, floorPlanId, mappingEntryId, pointType, synced',
      standaloneMaps: 'id, userId, name, synced',
      dropdownOptionsCache: 'id, category, sortOrder',
      productsCache: 'id, brand, sortOrder',
      typologyPrices: 'id, projectId, attraversamento, tipologicoId, [projectId+attraversamento], [projectId+attraversamento+tipologicoId]',
      sals: 'id, projectId, number, createdAt'
    });

    // Define schema v10 - add offline pin preferences table
    this.version(10).stores({
      projects: 'id, ownerId, *accessibleUsers, synced, updatedAt, archived, syncEnabled',
      mappingEntries: 'id, projectId, floor, createdBy, synced, timestamp',
      photos: 'id, mappingEntryId, uploaded',
      syncQueue: 'id, synced, timestamp, entityType, entityId',
      users: 'id, email, role',
      metadata: 'key',
      projectCachePrefs: 'projectId, offlinePinned, updatedAt',
      conflictHistory: 'id, timestamp, entityType, entityId, userNotified',
      floorPlans: 'id, projectId, floor, createdBy, synced, [projectId+floor]',
      floorPlanPoints: 'id, floorPlanId, mappingEntryId, pointType, synced',
      standaloneMaps: 'id, userId, name, synced',
      dropdownOptionsCache: 'id, category, sortOrder',
      productsCache: 'id, brand, sortOrder',
      typologyPrices: 'id, projectId, attraversamento, tipologicoId, [projectId+attraversamento], [projectId+attraversamento+tipologicoId]',
      sals: 'id, projectId, number, createdAt'
    });

    // Define schema v11 - add strutture (structures) table
    this.version(11).stores({
      projects: 'id, ownerId, *accessibleUsers, synced, updatedAt, archived, syncEnabled',
      mappingEntries: 'id, projectId, floor, createdBy, synced, timestamp',
      photos: 'id, mappingEntryId, uploaded',
      syncQueue: 'id, synced, timestamp, entityType, entityId',
      users: 'id, email, role',
      metadata: 'key',
      projectCachePrefs: 'projectId, offlinePinned, updatedAt',
      conflictHistory: 'id, timestamp, entityType, entityId, userNotified',
      floorPlans: 'id, projectId, floor, createdBy, synced, [projectId+floor]',
      floorPlanPoints: 'id, floorPlanId, mappingEntryId, pointType, synced',
      standaloneMaps: 'id, userId, name, synced',
      dropdownOptionsCache: 'id, category, sortOrder',
      productsCache: 'id, brand, sortOrder',
      typologyPrices: 'id, projectId, attraversamento, tipologicoId, [projectId+attraversamento], [projectId+attraversamento+tipologicoId]',
      sals: 'id, projectId, number, createdAt',
      // STRUTTURE
      structureEntries: 'id, projectId, floor, createdBy, synced, timestamp'
    });

    // Define schema v12 - aggiunge tabella authCache per PBKDF2 offline auth
    this.version(12).stores({
      projects: 'id, ownerId, *accessibleUsers, synced, updatedAt, archived, syncEnabled',
      mappingEntries: 'id, projectId, floor, createdBy, synced, timestamp',
      photos: 'id, mappingEntryId, uploaded',
      syncQueue: 'id, synced, timestamp, entityType, entityId',
      users: 'id, email, role',
      metadata: 'key',
      projectCachePrefs: 'projectId, offlinePinned, updatedAt',
      conflictHistory: 'id, timestamp, entityType, entityId, userNotified',
      floorPlans: 'id, projectId, floor, createdBy, synced, [projectId+floor]',
      floorPlanPoints: 'id, floorPlanId, mappingEntryId, pointType, synced',
      standaloneMaps: 'id, userId, name, synced',
      dropdownOptionsCache: 'id, category, sortOrder',
      productsCache: 'id, brand, sortOrder',
      typologyPrices: 'id, projectId, attraversamento, tipologicoId, [projectId+attraversamento], [projectId+attraversamento+tipologicoId]',
      sals: 'id, projectId, number, createdAt',
      structureEntries: 'id, projectId, floor, createdBy, synced, timestamp',
      // AUTH CACHE PBKDF2
      authCache: 'id, email'
    });

    // Define schema v13 - aggiunge tabella realtimeState per SSE cursor (appliedSeq, tabId)
    this.version(13).stores({
      projects: 'id, ownerId, *accessibleUsers, synced, updatedAt, archived, syncEnabled',
      mappingEntries: 'id, projectId, floor, createdBy, synced, timestamp',
      photos: 'id, mappingEntryId, uploaded',
      syncQueue: 'id, synced, timestamp, entityType, entityId',
      users: 'id, email, role',
      metadata: 'key',
      projectCachePrefs: 'projectId, offlinePinned, updatedAt',
      conflictHistory: 'id, timestamp, entityType, entityId, userNotified',
      floorPlans: 'id, projectId, floor, createdBy, synced, [projectId+floor]',
      floorPlanPoints: 'id, floorPlanId, mappingEntryId, pointType, synced',
      standaloneMaps: 'id, userId, name, synced',
      dropdownOptionsCache: 'id, category, sortOrder',
      productsCache: 'id, brand, sortOrder',
      typologyPrices: 'id, projectId, attraversamento, tipologicoId, [projectId+attraversamento], [projectId+attraversamento+tipologicoId]',
      sals: 'id, projectId, number, createdAt',
      structureEntries: 'id, projectId, floor, createdBy, synced, timestamp',
      authCache: 'id, email',
      // REALTIME STATE: sopravvive ai sync reset come authCache e metadata
      realtimeState: 'key'
    });

    // Define schema v14 - B10: photos[] embedded → photoIds[] (solo riferimenti).
    // Gli indici sono identici a v13 (photos/photoIds non sono indicizzati);
    // l'upgrade converte il campo e rimuove il duplicato. Le righe della tabella
    // `photos` (fonte di verità) restano intatte.
    this.version(14).stores({
      projects: 'id, ownerId, *accessibleUsers, synced, updatedAt, archived, syncEnabled',
      mappingEntries: 'id, projectId, floor, createdBy, synced, timestamp',
      photos: 'id, mappingEntryId, uploaded',
      syncQueue: 'id, synced, timestamp, entityType, entityId',
      users: 'id, email, role',
      metadata: 'key',
      projectCachePrefs: 'projectId, offlinePinned, updatedAt',
      conflictHistory: 'id, timestamp, entityType, entityId, userNotified',
      floorPlans: 'id, projectId, floor, createdBy, synced, [projectId+floor]',
      floorPlanPoints: 'id, floorPlanId, mappingEntryId, pointType, synced',
      standaloneMaps: 'id, userId, name, synced',
      dropdownOptionsCache: 'id, category, sortOrder',
      productsCache: 'id, brand, sortOrder',
      typologyPrices: 'id, projectId, attraversamento, tipologicoId, [projectId+attraversamento], [projectId+attraversamento+tipologicoId]',
      sals: 'id, projectId, number, createdAt',
      structureEntries: 'id, projectId, floor, createdBy, synced, timestamp',
      authCache: 'id, email',
      realtimeState: 'key'
    }).upgrade(async (tx) => {
      await tx.table('mappingEntries').toCollection().modify((e: any) => {
        e.photoIds = Array.isArray(e.photos) ? e.photos.map((p: any) => p.id) : (e.photoIds ?? []);
        delete e.photos;
      });
      await tx.table('structureEntries').toCollection().modify((e: any) => {
        e.photoIds = Array.isArray(e.photos) ? e.photos.map((p: any) => p.id) : (e.photoIds ?? []);
        delete e.photos;
      });
    });
  }
}

// Create and export database instance
export const db = new MappingDatabase();

// Helper function to generate UUIDs
export function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for Safari < 15.4 which lacks crypto.randomUUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
}

// Helper function to get current timestamp
export function now(): number {
  return Date.now();
}

// Initialize database
export async function initializeDatabase(): Promise<void> {
  try {
    await db.open();
    console.log('Database initialized successfully');

    // Set initial metadata
    const lastSyncKey = await db.metadata.get('lastSync');
    if (!lastSyncKey) {
      await db.metadata.put({ key: 'lastSync', value: 0 });
    }

    const currentUserKey = await db.metadata.get('currentUser');
    if (!currentUserKey) {
      await db.metadata.put({ key: 'currentUser', value: null });
    }
  } catch (error) {
    console.error('Failed to initialize database:', error);
    throw error;
  }
}

// Clear all data (for testing or reset)
// Get database statistics
export async function getDatabaseStats() {
  const [
    projectCount,
    mappingCount,
    photoCount,
    pendingSyncCount,
    userCount,
    floorPlanCount,
    floorPlanPointCount,
    standaloneMapCount,
    structureEntryCount
  ] = await Promise.all([
    db.projects.count(),
    db.mappingEntries.count(),
    db.photos.count(),
    db.syncQueue.where('synced').equals(0).count(),
    db.users.count(),
    db.floorPlans.count(),
    db.floorPlanPoints.count(),
    db.standaloneMaps.count(),
    db.structureEntries.count()
  ]);

  // Calculate approximate storage size
  const photos = await db.photos.toArray();
  const safePhotoStorageSize = photos.reduce((sum, photo) =>
    sum + (photo.blob?.size || 0) + (photo.thumbnailBlob?.size || 0), 0
  );

  const floorPlans = await db.floorPlans.toArray();
  const totalFloorPlanSize = floorPlans.reduce((sum, fp) =>
    sum + (fp.imageBlob?.size || 0) + (fp.thumbnailBlob?.size || 0), 0
  );

  const standaloneMaps = await db.standaloneMaps.toArray();
  const totalStandaloneMapSize = standaloneMaps.reduce((sum, sm) =>
    sum + (sm.imageBlob?.size || 0) + (sm.thumbnailBlob?.size || 0), 0
  );

  return {
    projects: projectCount,
    mappingEntries: mappingCount,
    structureEntries: structureEntryCount,
    photos: photoCount,
    pendingSync: pendingSyncCount,
    users: userCount,
    floorPlans: floorPlanCount,
    floorPlanPoints: floorPlanPointCount,
    standaloneMaps: standaloneMapCount,
    photoStorageBytes: safePhotoStorageSize,
    photoStorageMB: (safePhotoStorageSize / (1024 * 1024)).toFixed(2),
    floorPlanStorageBytes: totalFloorPlanSize,
    floorPlanStorageMB: (totalFloorPlanSize / (1024 * 1024)).toFixed(2),
    standaloneMapStorageBytes: totalStandaloneMapSize,
    standaloneMapStorageMB: (totalStandaloneMapSize / (1024 * 1024)).toFixed(2),
    totalStorageBytes: safePhotoStorageSize + totalFloorPlanSize + totalStandaloneMapSize,
    totalStorageMB: ((safePhotoStorageSize + totalFloorPlanSize + totalStandaloneMapSize) / (1024 * 1024)).toFixed(2)
  };
}
